import {useEffect, useState} from 'react';
import {CredentialCardProps, verifiedIconMap} from './credential_types.web';
import {styles} from './credential_styles.web';
import {HeadingCredentialChipColor} from '../../Headings/HeadingCredential/HeadingCredential_types.web';
import HeadingCredential from '../../Headings/HeadingCredential/headingCredential.web';
import Chip from '../../Chips/Chip/Chip.web';
import {useTheme} from '../../../../utils/theming/themeProvider';
import {isColorDark, isWhite} from '../../../../utils/colors';
import {canGetImage} from '../../../../utils/images';
import ImageError from '../../Icons/icons/imageError';
import Tooltip from '../../Tooltips/Tooltip/Tooltip.web';

const CredentialCard = (props: CredentialCardProps) => {
  const {
    id,
    className,
    state,
    issuer,
    verifiedIcon,
    headingCredential1,
    headingCredential2,
    headingCredential3,
    headingCredential4,
    headingCredential5,
    style,
    chip,
    display,
  } = props;

  // Styles
  const {colors, fontStyles} = useTheme();
  const topDataHeight = headingCredential1?.imageSlot?.length ? 44 : 104;

  const generalStyles = styles(
    topDataHeight,
    colors,
    fontStyles,
    display?.background_color,
    !!verifiedIcon,
    !!display?.hasGradient,
  );
  const [imageCanCharge, setImageCanCharge] = useState<boolean | undefined>();
  const [chargingImage, setChagingImage] = useState<boolean | undefined>(true);

  const headingCredsRangeColor =
    display?.text_color?.toLowerCase() === '#ffffff' ||
    (display?.background_color && isColorDark(display?.background_color))
      ? 'light'
      : display?.text_color?.toLowerCase() === '#000000' ||
        (display?.background_color && !isColorDark(display?.background_color))
      ? 'dark'
      : display?.background_color && isColorDark(display?.background_color)
      ? 'light'
      : 'dark';

  const headingCredsColor =
    display?.text_color?.length &&
    display?.text_color.toLowerCase() !== '#ffffff' &&
    display?.text_color.toLowerCase() !== '#000000'
      ? display?.text_color
      : undefined;

  // Animations with state
  const expanded = state === 'open';
  const [isExpanded, setIsExpanded] = useState(false);

  // Effect to toggle the expansion
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsExpanded(expanded);
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [expanded]);

  useEffect(() => {
    prepareIssuerImage();
  }, [issuer?.image]);

  // IssuerImage
  async function prepareIssuerImage() {
    const imageCharge = !!issuer?.image && (await canGetImage(issuer?.image));
    if (!!issuer?.image && imageCharge) {
      setImageCanCharge(true);
    } else {
      setImageCanCharge(false);
    }
    setChagingImage(false);
  }

  // Verified Icon
  const VerifiedIconComponent =
    verifiedIcon && verifiedIconMap[verifiedIcon?.icon];

  const credBgColorIsDark =
    display?.background_color &&
    (!display?.text_color || headingCredsRangeColor === 'light') &&
    isColorDark(display?.background_color);

  const verifiedIconCustomProps =
    verifiedIcon?.icon === 'check'
      ? {
          color: !credBgColorIsDark ? colors.success300 : colors.success100,
          style: 'filled' as 'filled',
          checkColor: !credBgColorIsDark
            ? colors.neutral100
            : colors.success300,
        }
      : {
          color: !credBgColorIsDark ? colors.alert300 : colors.alert100,
          style: 'filled' as 'filled',
          crossColor: !credBgColorIsDark ? colors.neutral100 : colors.alert300,
        };

  const chipIsGrey = isWhite(display?.background_color || colors.neutral100);

  return (
    <div
      id={id}
      className={className}
      style={{...generalStyles.credCardContainer, ...style}}>
      <div style={{...generalStyles.linearGradientOverlay}}>
        <div style={{...generalStyles.credCardMainContent}}>
          <div
            style={{
              ...generalStyles.credCardDetailRow,
              ...generalStyles.credCardTopData,
            }}>
            {issuer?.image ? (
              !!imageCanCharge ? (
                <img
                  src={issuer?.image}
                  alt="issuer"
                  style={{...generalStyles.credCardIssuerImage}}
                />
              ) : !chargingImage ? (
                <div
                  style={{
                    ...generalStyles.credIssuerImageError,
                  }}>
                  <ImageError
                    size={'24'}
                    color={
                      display?.text_color && !!headingCredsColor
                        ? display?.text_color
                        : credBgColorIsDark
                        ? colors.neutral300
                        : colors.neutral600
                    }
                  />
                  <p
                    style={{
                      color:
                        display?.text_color && !!headingCredsColor
                          ? display?.text_color
                          : credBgColorIsDark
                          ? colors.neutral300
                          : colors.neutral600,
                    }}>
                    {issuer?.failToLoadImageText || 'Fail to load image'}
                  </p>
                </div>
              ) : null
            ) : (
              <HeadingCredential
                type={'main'}
                align={'left'}
                rangeColor={headingCredsRangeColor}
                textColor={headingCredsColor}
                valueText={issuer?.name}
              />
            )}
            {((verifiedIcon && VerifiedIconComponent) || chip) && (
              <div style={{...generalStyles.chipAndVerifiedIconContainer}}>
                {chip && (
                  <Chip
                    {...chip}
                    color={chipIsGrey ? 'grey' : 'white'}
                    state={'enable'}
                    style={'filled'}
                    form={'square'}
                    chipSize={'small'}
                    showText={true}
                  />
                )}
                {verifiedIcon && VerifiedIconComponent && (
                  <div style={{...generalStyles.credCardVerifiedIcon}}>
                    {verifiedIcon?.tooltip ? (
                      <Tooltip {...verifiedIcon?.tooltip}>
                        <VerifiedIconComponent
                          size={'24'}
                          {...verifiedIconCustomProps}
                        />
                      </Tooltip>
                    ) : (
                      <VerifiedIconComponent
                        size={'24'}
                        {...verifiedIconCustomProps}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div style={{...generalStyles.credCardDetailRow}}>
            <HeadingCredential
              id={headingCredential1?.id}
              className={headingCredential1?.className}
              type={'main'}
              align={'left'}
              typeText={headingCredential1.typeText}
              imageSlot={
                headingCredential1?.imageSlot
                  ? {
                      image: headingCredential1?.imageSlot,
                    }
                  : undefined
              }
              rangeColor={headingCredsRangeColor}
              textColor={headingCredsColor}
              valueText={
                (!headingCredential1?.imageSlot &&
                  headingCredential1?.valueText) ||
                ''
              }
            />
            {headingCredential5?.chip && state !== 'open' && (
              <Chip
                color={headingCredential5?.chip?.color}
                state={'enable'}
                style={'filled'}
                form={'square'}
                chipSize={'medium'}
                tooltip={headingCredential5?.chip?.tooltip || undefined}
                text={headingCredential5?.chip?.text}
                containerStyle={{alignSelf: 'flex-end'}}
              />
            )}
          </div>
        </div>
        {state === 'open' ? (
          <div
            style={{
              ...generalStyles.credCardOpenContent,
              maxHeight: isExpanded ? '500px' : '0',
              marginTop: isExpanded ? '36px' : '0',
              height: isExpanded ? 'auto' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.5s ease, margin-top 0.5s ease',
            }}>
            <div style={{...generalStyles.credCardDetailRow}}>
              <HeadingCredential
                id={headingCredential2.id}
                className={headingCredential2.className}
                type={'content'}
                align={'left'}
                typeText={headingCredential2.typeText}
                rangeColor={headingCredsRangeColor}
                textColor={headingCredsColor}
                valueText={issuer?.name}
              />
              <HeadingCredential
                type={'content'}
                align={'right'}
                id={headingCredential3.id}
                className={headingCredential3.className}
                typeText={headingCredential3.typeText}
                rangeColor={headingCredsRangeColor}
                textColor={headingCredsColor}
                valueText={headingCredential3?.valueText}
              />
            </div>
            <div style={{...generalStyles.credCardDetailRow}}>
              <HeadingCredential
                id={headingCredential4.id}
                className={headingCredential4.className}
                type={'content'}
                align={'left'}
                typeText={headingCredential4?.typeText}
                rangeColor={headingCredsRangeColor}
                textColor={headingCredsColor}
                valueText={headingCredential4?.valueText}
              />
              {(headingCredential5?.valueText || headingCredential5?.chip) && (
                <HeadingCredential
                  type={'content'}
                  align={'right'}
                  id={headingCredential5.id}
                  className={headingCredential5.className}
                  typeText={headingCredential5?.typeText}
                  rangeColor={headingCredsRangeColor}
                  textColor={headingCredsColor}
                  chip={
                    headingCredential5?.chip
                      ? {
                          color: headingCredential5?.chip
                            ?.color as HeadingCredentialChipColor,
                          text: headingCredential5?.chip?.text || '',
                          tooltip:
                            headingCredential5?.chip?.tooltip || undefined,
                        }
                      : undefined
                  }
                  valueText={
                    (!headingCredential5?.chip &&
                      headingCredential5?.valueText) ||
                    ''
                  }
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CredentialCard;
